import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { authService } from '../services/auth.service';
import { ResetPasswordRequest, ConfirmResetPasswordRequest } from '../types/auth.types';

interface PasswordResetFormProps {
  onBackToLogin?: () => void;
  initialEmail?: string;
}

const steps = ['Введіть email', 'Введіть код і новий пароль'];

export const PasswordResetForm: React.FC<PasswordResetFormProps> = ({
                                                                      onBackToLogin,
                                                                      initialEmail = ''
                                                                    }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [email, setEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState('');

  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({
    code: '',
    newPassword: '',
    confirmPassword: '',
  });

  const validateEmail = (email: string) => {
    if (!email) {
      return 'Email обов\'язковий';
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      return 'Невірний формат email';
    }
    return '';
  };

  const validateStep2 = () => {
    const errors = {
      code: '',
      newPassword: '',
      confirmPassword: '',
    };
    let isValid = true;

    if (!code) {
      errors.code = 'Код обов\'язковий';
      isValid = false;
    }

    if (!newPassword) {
      errors.newPassword = 'Новий пароль обов\'язковий';
      isValid = false;
    } else if (newPassword.length < 6) {
      errors.newPassword = 'Пароль повинен містити мінімум 6 символів';
      isValid = false;
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Підтвердження пароля обов\'язкове';
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Паролі не збігаються';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailValidationError = validateEmail(email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }

    setLoading(true);
    setError('');
    setEmailError('');

    try {
      const requestData: ResetPasswordRequest = { email };
      await authService.requestPasswordReset(requestData);
      setSuccess('Код для відновлення пароля надіслано на ваш email');
      setActiveStep(1);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка при надсиланні коду');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep2()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const confirmData: ConfirmResetPasswordRequest = {
        email,
        code,
        newPassword,
      };
      await authService.confirmPasswordReset(confirmData);
      setSuccess('Пароль успішно змінено! Можете увійти з новим паролем.');
      setTimeout(() => {
        onBackToLogin?.();
      }, 2000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка при зміні пароля');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setFormErrors({ code: '', newPassword: '', confirmPassword: '' });
    setError('');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 2,
      }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        Відновлення пароля
      </Typography>

      <Box sx={{ width: '100%', maxWidth: 400 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {activeStep === 0 && (
          <Box component="form" onSubmit={handleRequestCode} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError('');
                if (error) setError('');
              }}
              autoComplete="email"
              autoFocus={!initialEmail}
              error={!!emailError}
              helperText={emailError || 'Введіть email для отримання коду відновлення'}
              InputLabelProps={{ shrink: true }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Надіслати код'}
            </Button>
          </Box>
        )}

        {activeStep === 1 && (
          <Box component="form" onSubmit={handleConfirmReset} noValidate>
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Код надіслано на: <strong>{email}</strong>
              </Typography>
            </Box>

            <TextField
              margin="normal"
              required
              fullWidth
              label="Код з email"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                if (formErrors.code) {
                  setFormErrors(prev => ({ ...prev, code: '' }));
                }
                if (error) setError('');
              }}
              placeholder="Введіть код"
              error={!!formErrors.code}
              helperText={formErrors.code || 'Введіть код з email'}
              autoFocus
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              label="Новий пароль"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (formErrors.newPassword) {
                  setFormErrors(prev => ({ ...prev, newPassword: '' }));
                }
              }}
              error={!!formErrors.newPassword}
              helperText={formErrors.newPassword}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              label="Підтвердіть новий пароль"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (formErrors.confirmPassword) {
                  setFormErrors(prev => ({ ...prev, confirmPassword: '' }));
                }
              }}
              error={!!formErrors.confirmPassword}
              helperText={formErrors.confirmPassword}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Назад
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ flex: 1 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Змінити пароль'}
              </Button>
            </Box>
          </Box>
        )}

        <Box textAlign="center" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Згадали пароль?{' '}
            <Link
              component="button"
              variant="body2"
              onClick={(e) => {
                e.preventDefault();
                onBackToLogin?.();
              }}
            >
              Увійти
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
